import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './Appointments.css';

const statuses = ['All', 'Scheduled', 'Confirmed', 'Arrived', 'In Progress', 'Completed', 'Cancelled', 'No Show'];
const priorities = ['Low', 'Normal', 'High', 'Urgent'];

const todayInput = () => new Date().toISOString().split('T')[0];

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value)).replace(/ /g, '-');
};

function Appointments({ onLogout }) {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState(todayInput());
  const [dateTo, setDateTo] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    clientId: '',
    vehicleInfoId: '',
    employeeId: '',
    appointmentDate: todayInput(),
    startTime: '09:00',
    endTime: '',
    serviceType: '',
    status: 'Scheduled',
    priority: 'Normal',
    source: 'Walk-in',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    vehicleText: '',
    notes: '',
  });

  const token = localStorage.getItem('token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.get('/api/appointments', {
        ...auth,
        params: {
          search: searchTerm,
          status: statusFilter,
          dateFrom,
          dateTo,
        },
      });
      setAppointments(response.data.appointments || []);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load appointments.');
    } finally {
      setLoading(false);
    }
  }, [auth, dateFrom, dateTo, searchTerm, statusFilter]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [clientRes, vehicleRes, employeeRes] = await Promise.all([
          axios.get('/api/clients', { ...auth, params: { pageSize: 500 } }),
          axios.get('/api/vehicles', { ...auth, params: { pageSize: 500 } }),
          axios.get('/api/time-tracking/employees', auth),
        ]);

        setClients(clientRes.data.clients || []);
        setVehicles(vehicleRes.data.vehicles || []);
        setEmployees(employeeRes.data.employees || []);
      } catch (error) {
        setMessage(error.response?.data?.message || 'Failed to load appointment options.');
      }
    };

    loadLookups();
  }, [auth]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const selectedClient = clients.find((client) => String(client.ClientId) === String(form.clientId));
  const selectedVehicle = vehicles.find((vehicle) => String(vehicle.VehicleInfoId) === String(form.vehicleInfoId));

  const changeVehicle = (vehicleInfoId) => {
    const vehicle = vehicles.find((item) => String(item.VehicleInfoId) === String(vehicleInfoId));
    setForm((current) => ({
      ...current,
      vehicleInfoId,
      clientId: vehicle?.ClientId ? String(vehicle.ClientId) : '',
      customerName: vehicle?.ClientName || '',
      customerPhone: vehicle?.ClientPhone || '',
      vehicleText: vehicle
        ? [vehicle.CarNumber, vehicle.Year, vehicle.Make, vehicle.Model].filter(Boolean).join(' ')
        : '',
    }));
  };

  const submitAppointment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const payload = {
        ...form,
        customerName: form.customerName || selectedClient?.ClientName || '',
        customerPhone: form.customerPhone || selectedClient?.Phone || '',
        vehicleText: form.vehicleText || (selectedVehicle
          ? [selectedVehicle.CarNumber, selectedVehicle.Year, selectedVehicle.Make, selectedVehicle.Model].filter(Boolean).join(' ')
          : ''),
      };

      const response = await axios.post('/api/appointments', payload, auth);
      setMessage(`Appointment ${response.data.appointmentNumber} saved.`);
      setForm((current) => ({
        ...current,
        serviceType: '',
        notes: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        vehicleText: '',
      }));
      await fetchAppointments();
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.[0]?.msg;
      setMessage(validationMessage || error.response?.data?.message || 'Failed to save appointment.');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (appointmentId, status) => {
    setMessage('');

    try {
      await axios.patch(`/api/appointments/${appointmentId}/status`, { status }, auth);
      await fetchAppointments();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to update appointment status.');
    }
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="appointments-page">
        <header className="appointments-header app-page-header">
          <div className="appointments-title-group app-title-group">
            <button className="app-back-button" type="button" onClick={() => navigate('/')} aria-label="Back to home" />
            <div className="app-title-copy">
              <h1>Appointments</h1>
              <p>Schedule customers, vehicles, service, and staff.</p>
            </div>
          </div>
          <div className="appointments-header-actions app-header-actions">
            <button type="button" onClick={fetchAppointments}>{loading ? 'Refreshing...' : 'Refresh'}</button>
          </div>
        </header>

        <section className="appointments-workspace">
          <form className="appointments-form" onSubmit={submitAppointment}>
            <h2>New Appointment</h2>

            <div className="appointments-field-grid">
              <label>
                Client
                <select value={form.clientId} onChange={(event) => updateForm('clientId', event.target.value)} disabled={Boolean(selectedVehicle?.ClientId)}>
                  <option value="">{form.vehicleInfoId ? 'No linked client found' : 'Select car number first'}</option>
                  {clients.map((client) => (
                    <option key={client.ClientId} value={client.ClientId}>
                      {client.ClientName} {client.Phone ? `- ${client.Phone}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Car Number
                <select value={form.vehicleInfoId} onChange={(event) => changeVehicle(event.target.value)}>
                  <option value="">Select car number</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.VehicleInfoId} value={vehicle.VehicleInfoId}>
                      {[vehicle.CarNumber, vehicle.Year, vehicle.Make, vehicle.Model].filter(Boolean).join(' ')}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Date
                <input type="date" value={form.appointmentDate} onChange={(event) => updateForm('appointmentDate', event.target.value)} required />
              </label>

              <label>
                Start
                <input type="time" value={form.startTime} onChange={(event) => updateForm('startTime', event.target.value)} required />
              </label>

              <label>
                End
                <input type="time" value={form.endTime} onChange={(event) => updateForm('endTime', event.target.value)} />
              </label>

              <label>
                Assign To
                <select value={form.employeeId} onChange={(event) => updateForm('employeeId', event.target.value)}>
                  <option value="">Unassigned</option>
                  {employees.map((employee) => (
                    <option key={employee.EmployeeId} value={employee.EmployeeId}>
                      {employee.FullName}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Service
                <input value={form.serviceType} onChange={(event) => updateForm('serviceType', event.target.value)} placeholder="Oil change, diagnosis, inspection" required />
              </label>

              <label>
                Priority
                <select value={form.priority} onChange={(event) => updateForm('priority', event.target.value)}>
                  {priorities.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                </select>
              </label>
            </div>

            <div className="appointments-field-grid compact">
              <input value={form.customerName} onChange={(event) => updateForm('customerName', event.target.value)} placeholder="Walk-in customer name" />
              <input value={form.customerPhone} onChange={(event) => updateForm('customerPhone', event.target.value)} placeholder="Phone" />
              <input value={form.vehicleText} onChange={(event) => updateForm('vehicleText', event.target.value)} placeholder="Vehicle text" />
              <select value={form.source} onChange={(event) => updateForm('source', event.target.value)}>
                <option value="Walk-in">Walk-in</option>
                <option value="Phone">Phone</option>
                <option value="Facebook">Facebook</option>
                <option value="Website">Website</option>
                <option value="Repeat Customer">Repeat Customer</option>
              </select>
            </div>

            <textarea value={form.notes} onChange={(event) => updateForm('notes', event.target.value)} placeholder="Notes" />

            <button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Appointment'}
            </button>
          </form>

          <section className="appointments-list">
            <div className="appointments-toolbar">
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search client, phone, plate, service"
              />
            </div>

            <div className="appointments-table-scroll">
              <table className="appointments-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Client</th>
                    <th>Vehicle</th>
                    <th>Service</th>
                    <th>Assigned</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => {
                    const vehicle = [appointment.CarNumber, appointment.Year, appointment.Make, appointment.Model].filter(Boolean).join(' ') || appointment.VehicleText;
                    return (
                      <tr key={appointment.AppointmentId}>
                        <td>{appointment.AppointmentNumber}</td>
                        <td>{formatDate(appointment.AppointmentDate)}</td>
                        <td>{appointment.EndTime ? `${appointment.StartTime} - ${appointment.EndTime}` : appointment.StartTime}</td>
                        <td>
                          <strong>{appointment.ClientName || '-'}</strong>
                          <span>{appointment.Phone || ''}</span>
                        </td>
                        <td>{vehicle || '-'}</td>
                        <td>
                          <strong>{appointment.ServiceType}</strong>
                          <span>{appointment.Priority}</span>
                        </td>
                        <td>{appointment.EmployeeName || '-'}</td>
                        <td>
                          <select value={appointment.Status} onChange={(event) => changeStatus(appointment.AppointmentId, event.target.value)}>
                            {statuses.filter((status) => status !== 'All').map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  {appointments.length === 0 && (
                    <tr>
                      <td colSpan="8" className="appointments-empty">
                        {loading ? 'Loading appointments...' : 'No appointments found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </section>

        {message && <div className="appointments-message">{message}</div>}
      </main>
    </div>
  );
}

export default Appointments;
